import { Plus } from 'lucide-react'
import { formatCurrency } from '@/utils/format-currency'
import { formatDate } from '@/utils/format-date'
import { Badge } from '@/components/ui/Badge'
import { Button } from '@/components/ui/Button'
import type { Proposta } from '@/types'
import { ItemActions } from './ItemActions'
import { ExportSection } from './ExportSection'
import { proposalStatusConfig } from './types'

interface ProposalsListProps {
  proposals: Proposta[]
  loading: boolean
  canEdit: boolean
  allowNew?: boolean
  dealEtapa?: string
  showForm: boolean
  editingId: string | null
  onToggleForm: () => void
  onEdit: (proposta: Proposta) => void
  onDelete: (id: string) => void
  onCopyLink: (proposta: Proposta) => void
  onOpenStudio?: (item: import('@/types').PropostaItem) => void
  onExportPPTX?: (proposta?: Proposta) => void
  onExportPDF?: (proposta?: Proposta) => void
}

export function ProposalsList({
  proposals,
  loading,
  canEdit,
  allowNew = true,
  dealEtapa,
  showForm,
  editingId,
  onToggleForm,
  onEdit,
  onDelete,
  onCopyLink,
  onOpenStudio,
  onExportPPTX,
  onExportPDF,
}: ProposalsListProps) {
  return (
    <div className="glass-card p-4 space-y-3">
      <div className="flex items-center justify-between gap-2">
        <div className="min-w-0">
          <h3 className="text-xs font-bold text-accent uppercase tracking-wider">
            Propostas salvas ({proposals.length})
          </h3>
          <p className="text-[10px] text-text-muted mt-0.5 leading-snug">
            Rascunhos e envios gravados neste negócio — independente de combo ou opções.
          </p>
        </div>
        {canEdit && allowNew && (
          <Button variant="ghost" size="sm" icon={<Plus size={13} />} onClick={onToggleForm} className="shrink-0">
            {showForm && !editingId ? 'Fechar editor' : 'Nova proposta'}
          </Button>
        )}
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-6">
          <div className="w-5 h-5 border-2 border-accent/30 border-t-accent rounded-full animate-spin" />
        </div>
      ) : proposals.length > 0 ? (
        <div className="space-y-2">
          {proposals.map((p) => {
            const statusCfg = proposalStatusConfig[p.status] ?? proposalStatusConfig.rascunho
            const isHistorical =
              dealEtapa === 'renegociacao' &&
              (p.status === 'enviada' || p.status === 'aceita')
            return (
              <div key={p.id} className="p-3 rounded-lg bg-white/[0.03] border border-white/[0.06] space-y-2">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-bold text-text-primary">{formatCurrency(p.totalFinal)}</span>
                    <Badge variant={statusCfg.variant}>{statusCfg.label}</Badge>
                    {isHistorical && (
                      <Badge variant="muted" className="text-[9px]">Histórico</Badge>
                    )}
                  </div>
                  <ItemActions
                    proposta={p}
                    canEdit={canEdit}
                    dealEtapa={dealEtapa}
                    onEdit={onEdit}
                    onDelete={onDelete}
                    onCopyLink={onCopyLink}
                    onOpenStudio={onOpenStudio}
                    onExportPPTX={onExportPPTX}
                  />
                </div>
                <div className="flex items-center gap-3 text-xs text-text-label">
                  <span>{formatDate(p.createdAt)}</span>
                  <span>·</span>
                  <span>{p.validade} dias de validade</span>
                  {p.itens?.length > 0 && (
                    <><span>·</span><span>{p.itens.length} {p.itens.length === 1 ? 'item' : 'itens'}</span></>
                  )}
                  {p.desconto > 0 && (
                    <><span>·</span><span className="text-emerald-400">{p.desconto}% desc.</span></>
                  )}
                </div>
                {p.observacoes && <p className="text-xs text-text-muted line-clamp-2">{p.observacoes}</p>}
              </div>
            )
          })}
        </div>
      ) : (
        !showForm && (
          <p className="text-sm text-text-label italic py-2">
            Nenhuma proposta salva. Use &quot;Nova proposta&quot; para montar planos e enviar.
          </p>
        )
      )}

      <ExportSection
        hasProposals={proposals.length > 0}
        proposal={proposals[0]}
        onExportPPTX={onExportPPTX}
        onExportPDF={onExportPDF}
      />
    </div>
  )
}
